import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { stripFormatting } from "../../utils/numberGrouping";
import { GroupedNumberInput } from "../common/GroupedNumberInput";

const useStyles = makeStyles({
  wrap: {
    position: "relative",
    display: "flex",
    alignItems: "stretch",
    width: "100%",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "1rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "36px",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "120ms",
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
    "::placeholder": {
      color: "#c3bcc8",
    },
  },
  inputPlain: {
    paddingRight: "12px",
    textAlign: "left",
  },
  inputNumber: {
    paddingRight: "12px",
    textAlign: "right",
  },
  suffix: {
    position: "absolute",
    right: "12px",
    top: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    color: BRAND.muted,
    pointerEvents: "none",
  },
});

type Mode = "currency" | "number" | "text";

type Props = {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  mode?: Mode;
  /**
   * Zugänglicher Name des Feldes. Nötig, weil die Beschriftung im Wizard ein
   * `<span>` ohne `htmlFor` ist — ohne diesen Namen liest ein Screenreader nur
   * „Eingabefeld, leer“.
   */
  ariaLabel?: string;
};

const CurrencyInput = ({ value, onChange, placeholder, mode = "currency", ariaLabel }: Props) => {
  const styles = useStyles();
  const inputClass =
    mode === "currency"
      ? styles.input
      : mode === "number"
      ? `${styles.input} ${styles.inputNumber}`
      : `${styles.input} ${styles.inputPlain}`;

  // Nur melden, was den Wert wirklich ändert: `setAnswer` erzeugt oben immer ein
  // neues `answers`-Objekt und stößt damit den debounced Entwurfs-Autosave an.
  // Ohne diesen Vergleich schriebe schon das bloße Betreten und Verlassen eines
  // leeren Feldes einen Entwurf — der Kunde läse „Entwurf gespeichert um …“,
  // ohne etwas erfasst zu haben.
  const emit = (next: string) => {
    if (next !== value) onChange(next);
  };

  // Numerische Modi: Anzeige, Gruppierung und Cursor-Arithmetik liegen im
  // GroupedNumberInput — es gruppiert WÄHREND der Eingabe, statt wie vorher erst
  // beim Blur zu formatieren. Styling und €-Overlay bleiben hier.
  if (mode === "currency" || mode === "number") {
    return (
      <div className={styles.wrap}>
        <GroupedNumberInput
          className={inputClass}
          placeholder={placeholder}
          value={value}
          ariaLabel={ariaLabel}
          // Der Wizard hält seine Antworten als STRINGS ohne Tausenderpunkte
          // (`stripFormatting`) — so liegen sie im Entwurf und im `otherInfo`-Blob,
          // und so lesen sie `formatCurrency` und `parseEuroStrict` wieder.
          // Deshalb beide Kanäle: `onTextChange` hält den Fortschrittsbalken und
          // den debounced gespeicherten Entwurf während des Tippens mit …
          onTextChange={(text) => emit(stripFormatting(text))}
          // … und der Blur schreibt die kanonische Form (aus „1.000,“ wird „1000“,
          // aus Unfug ein leeres Feld).
          onChange={(next) => emit(next === undefined ? "" : String(next).replace(".", ","))}
        />
        {mode === "currency" && <span className={styles.suffix}>€</span>}
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <input
        type="text"
        inputMode="text"
        className={inputClass}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
};

export default CurrencyInput;
